import { useEffect, useCallback } from 'react';
import { ConfiguratorWizard } from './components/wizard/ConfiguratorWizard';
import { AIAssistant } from './components/ai';
import { useHistoryKeyboardShortcuts } from './components/history';
import { ToastProvider, useToast } from './components/ui';
import { useConfigurator, selectCanGoNext, selectCanGoBack } from './hooks/useConfigurator';

function AppContent() {
  const handleHistoryKeyboard = useHistoryKeyboardShortcuts({ showToast: true });
  const { info } = useToast();
  const { goNext, goBack } = useConfigurator();
  const canGoNext = useConfigurator(selectCanGoNext);
  const canGoBack = useConfigurator(selectCanGoBack);

  // Navigation keyboard handler
  const handleNavigation = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Alt + Left/Right for navigation
      if (e.altKey && e.key === 'ArrowRight' && canGoNext) {
        e.preventDefault();
        goNext();
        info('Next step', 1500);
      }
      if (e.altKey && e.key === 'ArrowLeft' && canGoBack) {
        e.preventDefault();
        goBack();
        info('Previous step', 1500);
      }
    },
    [goNext, goBack, canGoNext, canGoBack, info]
  );

  // Register global keyboard shortcuts
  useEffect(() => {
    window.addEventListener('keydown', handleHistoryKeyboard);
    window.addEventListener('keydown', handleNavigation);
    return () => {
      window.removeEventListener('keydown', handleHistoryKeyboard);
      window.removeEventListener('keydown', handleNavigation);
    };
  }, [handleHistoryKeyboard, handleNavigation]);

  // Listen for undo/redo events and show toasts
  useEffect(() => {
    const handleUndo = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      info(detail?.label ? `Undo: ${detail.label}` : 'Undo', 2000);
    };

    const handleRedo = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      info(detail?.label ? `Redo: ${detail.label}` : 'Redo', 2000);
    };

    window.addEventListener('hermit:undo', handleUndo);
    window.addEventListener('hermit:redo', handleRedo);
    return () => {
      window.removeEventListener('hermit:undo', handleUndo);
      window.removeEventListener('hermit:redo', handleRedo);
    };
  }, [info]);

  return (
    <>
      <ConfiguratorWizard />
      <AIAssistant />
    </>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}
